import { BLEND_MODES, Container, Graphics, IDestroyOptions, Matrix, Renderer, Sprite, Texture, filters } from "pixi.js"
import { findRendererApp } from "./renderer_app"

/**
 * Graphics shown through a blur that is baked once, after drawing, instead of filtered every frame.
 *
 * A live BlurFilter costs several offscreen passes and render-target switches per frame, and every
 * visible storey carries its own cues. The artwork changes only on a full map render, so soften()
 * renders it through the blur into a texture at that point, outside the frame's filter stack.
 * Baking lazily inside the frame (cacheAsBitmap) is unsafe here: pixi 6 sends a nested filter's
 * output to the enclosing filter's target, and the world surface sits inside the sight mask.
 *
 * The bake itself waits for the next animation frame. The same filtered render-to-texture made from
 * inside the game's frame update (where soften() is called) comes out EMPTY in pixi 6.2 -- measured:
 * 0 opaque pixels there, 33155 for the identical bake between frames. Until it lands (and headless,
 * with no renderer), the drawing shows through a live blur, so no frame goes without it -- or, for a
 * caller that would rather wait (BakedGroups: shadows swapped whole), soften()'s `ready` says when.
 */
export class SoftenedGraphics extends Graphics {
    readonly blur: InstanceType<typeof filters.BlurFilter>
    private baked?: Sprite
    private pendingBake?: number
    private stamps: { sprite: Sprite; matrix: Matrix; tint: number }[] = []
    /** Where the stamps may land (maskStamps): rectangles in local space; undefined for anywhere. */
    private stampArea?: { x: number; y: number; width: number; height: number }[]

    /**
     * `blend` is how the softened picture lands on what is under it (MULTIPLY for shade: the floor's
     * own colours times the shade's), kept by the live blur and by the bake. `resolution` is the bake's
     * (and the live blur's) pixels per world pixel: soft art loses nothing at half, and costs a quarter.
     */
    constructor(strength: number, quality: number, private readonly blend: BLEND_MODES = BLEND_MODES.NORMAL, resolution = 1) {
        super()
        this.blur = new filters.BlurFilter(strength, quality, resolution)
        // Only what lands on the world blends: the drawing itself renders plainly into the blur's buffer (a
        // MULTIPLY into that empty buffer drew nothing, so a re-drawn shadow vanished until its bake).
        this.blur.blendMode = blend
    }

    /**
     * Art drawn and baked along with the drawing, through `matrix` in local space: a shadow cast by a
     * picture rather than a shape. It shows live, under the same blur, until the bake lands. The
     * texture stays the caller's.
     */
    stamp(texture: Texture, matrix: Matrix, tint = 0xffffff): void {
        const sprite = new Sprite(texture)
        sprite.transform.setFromMatrix(matrix)
        sprite.tint = tint
        this.addChild(sprite)
        this.stamps.push({ sprite, matrix, tint })
    }

    /**
     * How the picture lands on what is under it (by default the `blend` it was made with). BakedGroups
     * lands its groups NORMAL into one buffer while it fades them, and multiplies that buffer once.
     */
    setLanding(blend: BLEND_MODES = this.blend): void {
        this.blur.blendMode = blend
        if (this.baked) this.baked.blendMode = blend
    }

    /**
     * Keep the stamps inside these rectangles (local space), applied when baking: a picture cannot be cut
     * like a polygon, and a live mask would cost a stencil pass every frame.
     */
    maskStamps(area: { x: number; y: number; width: number; height: number }[] | undefined): void {
        this.stampArea = area
    }

    /** Call after drawing. `ready` runs once the bake has landed (at once when there is nothing to bake, or no renderer). */
    soften(ready?: () => void): void {
        this.discardBake()
        if (!this.geometry.graphicsData.length && !this.stamps.length) { ready?.(); return }
        this.filters = [this.blur]
        const renderer = findRendererApp()?.renderer as Renderer | undefined
        if (!renderer || typeof requestAnimationFrame === "undefined") { ready?.(); return }
        this.pendingBake = requestAnimationFrame(() => {
            this.pendingBake = undefined
            if (this.destroyed) return
            this.bake(renderer)
            ready?.()
        })
    }

    clear(): this {
        this.discardBake()
        this.stamps.forEach(({ sprite }) => sprite.destroy())
        this.stamps = []
        return super.clear()
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.discardBake()
        this.blur.destroy()
        super.destroy(options)
    }

    protected _render(renderer: Renderer): void {
        // Once baked, only the softened child is drawn.
        if (!this.baked) super._render(renderer)
    }

    private bake(renderer: Renderer): void {
        // Nothing left to soften (cleared since): no picture. A bake of nothing is still a texture the blur's
        // padding wide at the origin, and it stretched its storey's bounds from the map's corner to its art.
        if (!this.geometry.graphicsData.length && !this.stamps.length) return
        // generateTexture keeps the world transform of a parented object, which would bake the camera in.
        let brush: Container = new Graphics(this.geometry)
        if (this.stamps.length && (!this.stampArea || this.stampArea.length)) {
            const drawing = brush
            brush = new Container()
            brush.addChild(drawing)
            const stamps = new Container()
            this.stamps.forEach(({ sprite, matrix, tint }) => {
                const copy = new Sprite(sprite.texture)
                copy.transform.setFromMatrix(matrix)
                copy.tint = tint
                stamps.addChild(copy)
            })
            if (this.stampArea) {
                const mask = new Graphics().beginFill(0xffffff, 1)
                this.stampArea.forEach(({ x, y, width, height }) => mask.drawRect(x, y, width, height))
                mask.endFill()
                stamps.addChild(mask)
                stamps.mask = mask
            }
            brush.addChild(stamps)
        }
        // The picture itself is baked plainly; only the baked sprite lands with `blend` (a MULTIPLY into
        // an empty texture would bake nothing but black).
        this.blur.blendMode = BLEND_MODES.NORMAL
        brush.filters = [this.blur]
        const region = brush.getLocalBounds().clone().pad(this.blur.padding)
        region.ceil()
        const texture = renderer.generateTexture(brush, { region, resolution: this.blur.resolution })
        this.blur.blendMode = this.blend
        brush.filters = null
        brush.destroy({ children: true })
        this.filters = null
        this.stamps.forEach(({ sprite }) => { sprite.visible = false })
        this.baked = new Sprite(texture)
        this.baked.blendMode = this.blend
        this.baked.position.set(region.x, region.y)
        this.addChild(this.baked)
    }

    private discardBake(): void {
        if (this.pendingBake !== undefined) {
            cancelAnimationFrame(this.pendingBake)
            this.pendingBake = undefined
        }
        this.filters = null
        this.stamps.forEach(({ sprite }) => { sprite.visible = true })
        if (!this.baked) return
        this.baked.destroy({ texture: true, baseTexture: true })
        this.baked = undefined
    }
}
